import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { MapPin, Shield, PlayCircle, CheckCircle2, ArrowRight, AlertCircle } from "lucide-react";
import GpsActivateButton from "./GpsActivateButton";

/**
 * Onboarding mostrato UNA SOLA VOLTA per ogni caccia al tesoro, appena l'utente
 * la avvia. Segue il flow del PDF Odismap sezione 2:
 *   1) Come si gioca
 *   2) Attiva la posizione (skip se GPS già attivo)
 *   3) Regole del luogo
 * A conferma finale → chiama onComplete() che notifica il backend.
 */
export default function RouteOnboarding({
  open,
  onComplete,
  onGpsActivate,
  gpsActive,
  gpsBusy,
  routeTitle,
}) {
  const [step, setStep] = useState(0);
  const [rules, setRules] = useState({ adult: false, place: false, all_rules: false });
  const allAccepted = rules.adult && rules.place && rules.all_rules;

  useEffect(() => {
    if (open) {
      setStep(0);
      setRules({ adult: false, place: false, all_rules: false });
    }
  }, [open]);

  const toggle = (key) => setRules((r) => ({ ...r, [key]: !r[key] }));

  const gpsStep = 1;
  const stepsCount = 3;

  const next = () => setStep((s) => Math.min(s + 1, stepsCount - 1));

  return (
    <Dialog open={open}>
      <DialogContent
        className="rounded-3xl bg-parchment border-outline max-w-lg w-[95vw] p-6"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="route-onboarding"
      >
        {/* Accessibility: Dialog richiede un titolo. Usiamo srOnly per non duplicare i titoli visivi dei singoli step. */}
        <DialogTitle className="sr-only">Onboarding caccia al tesoro</DialogTitle>
        <DialogDescription className="sr-only">
          Passa attraverso i 3 step di onboarding: come si gioca, attivazione della posizione, regole del luogo.
        </DialogDescription>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mb-4" data-testid="onboarding-progress">
          {Array.from({ length: stepsCount }).map((_, i) => (
            <span
              key={`onboarding-dot-${i}`}
              className={`h-2 rounded-full transition-all ${i === step ? "w-8 bg-gold" : "w-2 bg-outline"}`}
            />
          ))}
        </div>

        {/* Logo mini */}
        <div className="flex items-center justify-center mb-3">
          <img
            src="/odismap-logo-transparent.png"
            alt="Odismap"
            className="h-14 w-auto"
            draggable={false}
          />
        </div>

        {/* Step 0: Come si gioca */}
        {step === 0 && (
          <div className="space-y-4" data-testid="onboarding-step-play">
            <div className="text-center">
              <PlayCircle className="mx-auto text-gold-hover mb-2" size={40} />
              <h2 className="font-display font-extrabold text-2xl text-ink-900">Come si gioca</h2>
              <p className="text-sm text-ink-500 font-bold mt-1">{routeTitle}</p>
            </div>
            <div className="space-y-2 text-sm text-ink-800">
              <div className="flex items-start gap-3 p-3 bg-white rounded-2xl border border-outline">
                <span className="w-7 h-7 rounded-full bg-gold text-ink-900 font-display font-extrabold flex items-center justify-center shrink-0">1</span>
                <div><strong>Segui l'indizio</strong> e raggiungi la tappa. La distanza è mostrata dal GPS in tempo reale.</div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white rounded-2xl border border-outline">
                <span className="w-7 h-7 rounded-full bg-gold text-ink-900 font-display font-extrabold flex items-center justify-center shrink-0">2</span>
                <div><strong>Clicca "Sei nell'area"</strong> quando ci sei: scoprirai la storia del luogo.</div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white rounded-2xl border border-outline">
                <span className="w-7 h-7 rounded-full bg-gold text-ink-900 font-display font-extrabold flex items-center justify-center shrink-0">3</span>
                <div><strong>Rispondi alle sfide</strong> per guadagnare punti. Ogni errore toglie punti dal tuo totale!</div>
              </div>
            </div>
            <button
              onClick={next}
              className="btn-tactile w-full flex items-center justify-center gap-2"
              data-testid="onboarding-next-play"
            >
              Continua <ArrowRight size={18}/>
            </button>
          </div>
        )}

        {/* Step 1: Attiva la posizione */}
        {step === gpsStep && (
          <div className="space-y-4" data-testid="onboarding-step-gps">
            <div className="text-center">
              <MapPin className="mx-auto text-compass mb-2" size={40} />
              <h2 className="font-display font-extrabold text-2xl text-ink-900">Attiva la posizione</h2>
            </div>
            {gpsActive ? (
              <div className="flex items-center gap-3 p-4 rounded-2xl bg-forest/10 border-2 border-forest text-ink-800" data-testid="onboarding-gps-active">
                <CheckCircle2 className="text-forest shrink-0" size={24}/>
                <div>
                  <div className="font-bold text-forest">GPS attivo!</div>
                  <div className="text-xs">Il tuo dispositivo condivide la posizione con Odismap.</div>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-ink-800 text-center">
                  Per giocare è <strong>fondamentale</strong> la tua posizione: ti serve per raggiungere le tappe della caccia al tesoro.
                </p>
                <GpsActivateButton onRequest={onGpsActivate} busy={gpsBusy} variant="primary" />
                <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-2xl text-xs text-ink-700">
                  <AlertCircle className="text-yellow-700 shrink-0 mt-0.5" size={16}/>
                  <div>Se il browser blocca la posizione, usa la <strong>guida all'attivazione</strong> mostrata nel bottone.</div>
                </div>
              </>
            )}
            <button
              onClick={next}
              disabled={!gpsActive}
              className="btn-tactile w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="onboarding-next-gps"
            >
              {gpsActive ? <>Continua <ArrowRight size={18}/></> : "Attiva la posizione per continuare"}
            </button>
          </div>
        )}

        {/* Step 2: Regole del luogo — accettazione obbligatoria delle 3 condizioni */}
        {step === 2 && (
          <div className="space-y-4" data-testid="onboarding-step-rules">
            <div className="text-center">
              <Shield className="mx-auto text-forest mb-2" size={40} />
              <h2 className="font-display font-extrabold text-2xl text-ink-900">Le regole del luogo</h2>
              <p className="text-sm text-ink-500 mt-1">Spunta ogni regola per accettare e iniziare</p>
            </div>
            <div className="space-y-2 text-sm text-ink-800">
              {[
                { key: "adult", label: (<>Gioco insieme a un <strong>adulto maggiorenne</strong> che mi supervisiona.</>) },
                { key: "place", label: (<>Rispetto il <strong>luogo</strong>: resto sui percorsi pedonali, non entro in zone recintate e non calpesto le aiuole.</>) },
                { key: "all_rules", label: (<>Rispetterò <strong>tutte le regole</strong> del luogo.</>) },
              ].map(({ key, label }) => (
                <label
                  key={key}
                  htmlFor={`rule-${key}`}
                  className={`flex items-start gap-3 p-3 rounded-2xl border-2 cursor-pointer transition-all ${
                    rules[key]
                      ? "bg-forest/5 border-forest"
                      : "bg-white border-outline hover:border-forest/40"
                  }`}
                  data-testid={`rule-${key}-row`}
                >
                  <input
                    id={`rule-${key}`}
                    type="checkbox"
                    checked={rules[key]}
                    onChange={() => toggle(key)}
                    className="mt-0.5 w-5 h-5 rounded border-2 border-forest/60 text-forest focus:ring-forest cursor-pointer shrink-0 accent-forest"
                    data-testid={`rule-${key}-checkbox`}
                  />
                  <div className="flex-1 leading-snug">{label}</div>
                </label>
              ))}
            </div>
            {!allAccepted && (
              <div className="text-xs text-ink-500 text-center italic" data-testid="rules-hint">
                Per accettare le condizioni di gioco spunta tutte le regole.
              </div>
            )}
            <button
              onClick={onComplete}
              disabled={!allAccepted}
              className="btn-tactile w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="onboarding-confirm-rules"
            >
              {allAccepted ? "Confermo le regole e inizio!" : "Accetta tutte le regole per continuare"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
